import {
  NutritionixBrandedItem,
  NutritionixCommonItem,
  NutritionixItemWithNutrients,
} from '@src/services/Nutritionix/types'

export const isCommonIngredient = (item: unknown): item is NutritionixCommonItem => {
  const { tag_id: tagId } = item as NutritionixCommonItem

  return !!tagId
}

export const isBrandedIngredient = (item: unknown): item is NutritionixBrandedItem => {
  const { brand_name: brandName, nix_item_id: nutritionixId } = item as NutritionixBrandedItem

  return !!(brandName && nutritionixId)
}

export const isIngredientWithNutrients = (item: unknown): item is NutritionixItemWithNutrients => {
  const { alt_measures: altMeasures } = item as NutritionixItemWithNutrients

  return !!altMeasures
}

// Invisible placeholders that reach us in meal descriptions and never carry
// meaning in a food query. U+FFFC stands in for an object the text stack could
// not inline; U+FFFD for a character it could not decode. Seen in the wild in
// a query of "Coffee ￼", which Nutritionix answered with a 500. What puts
// the character there is unconfirmed — pasting an image was ruled out (it
// freezes the app before any request is made), leaving dictation and the
// AI photo-description flow as the likely candidates. Hence the general
// filter below rather than a fix targeted at one source.
const INVISIBLE_PLACEHOLDERS = /[￼�]/g

// Keep only what the natural-language parser can act on: letters and numbers
// in any script, whitespace, and the punctuation that carries meaning in a
// food description — "1/3 cup", "2.5 oz", "Ben & Jerry's", "chips (baked)".
// Everything else becomes a space rather than being deleted, so removing a
// character can't fuse two words together.
const UNPARSEABLE_CHARACTERS = /[^\p{L}\p{N}\s.,'/&()-]/gu

// Nutritionix returns 500 rather than 400 for queries it can't parse, so bad
// input surfaces as a server error we can't distinguish from a real outage.
// Strip what we know breaks it before sending.
export const sanitizeNaturalLanguageQuery = (query: string) =>
  query
    .replace(INVISIBLE_PLACEHOLDERS, ' ')
    .replace(UNPARSEABLE_CHARACTERS, ' ')
    // Collapse first so a multi-line query becomes the single line the parser
    // expects, and the trailing-"-" check below sees the real end of the query.
    .replace(/\s+/g, ' ')
    // Trailing "-" trips the parser ("Banana - 1, Apple - " → "Banana 1, Apple").
    // Runs last: the filters above can expose a "-" that was not previously last.
    .replace(/\s*-\s*$/, '')
    .trim()
